import { Rive } from '@rive-app/webgl2';

/**
 * Simple Wine Regions Map using Rive
 */
class WineRegionsMap {
	constructor() {
		this.canvas = null;
		this.container = null;
		this.rive = null;
		this.vmi = null;
		this.navigating = false;
		this.regionsData = this.getWineRegionsData();

		this.init();
	}

	init() {
		// Check if map container exists on this page
		this.container = document.getElementById('map-container');
		this.canvas = document.getElementById('rive-canvas');

		if (!this.container || !this.canvas) {
			return; // Map not on this page
		}

		this.setupCanvas();
		this.loadRiveMap();
	}

	setupCanvas() {
		// Function to resize canvas to match container
		this.resizeCanvas = () => {
			const containerRect = this.container.getBoundingClientRect();
			this.canvas.width = containerRect.width;
			this.canvas.height = containerRect.height;
			this.canvas.style.width = containerRect.width + 'px';
			this.canvas.style.height = containerRect.height + 'px';
		};

		// Initial resize
		this.resizeCanvas();

		// Handle window resize
		window.addEventListener('resize', () => {
			this.resizeCanvas();
			if (this.rive) {
				this.rive.resizeDrawingSurfaceToCanvas();
			}
		});
	}

	loadRiveMap() {
		// Append a cache-busting query in dev-like hostnames to ensure latest .riv is loaded
		let baseSrc = '/dist/rive/sawia_map.riv';
		if (window.innerWidth < 1024) {
			baseSrc = '/dist/rive/sawia_map_mobile.riv';
		}
		let riveSrc = baseSrc;
		try {
			const isDevHost = typeof window !== 'undefined' && window.location && /localhost|127\.0\.0\.1|\.local|\.test/.test(window.location.hostname);
			if (isDevHost) {
				riveSrc = `${baseSrc}?v=${Date.now()}`;
			}
		} catch (e) {
			// No-op if window/location is unavailable
		}

		this.rive = new Rive({
			src: riveSrc,
			canvas: this.canvas,
			autoplay: true,
			autoBind: true,
			artboard: 'map',
			stateMachines: 'State Machine 1',
			onLoad: () => this.onRiveLoaded(),
			onLoadError: (error) => {
				console.error('Error loading Rive map:', error);
			}
		});
	}

	onRiveLoaded() {
		this.vmi = this.rive.viewModelInstance;

		// Set initial view
		this.vmi.enum('currentView').value = 'State';

		// Set mobile detection
		try {
			this.vmi.boolean('isMobile').value = window.innerWidth <= 768;
		} catch (e) {}

		// Set up view control buttons
		this.setupViewButtons();

		// Populate region data
		this.populateRegionData();

		// Initial canvas resize
		this.rive.resizeDrawingSurfaceToCanvas();
	}

	setupViewButtons() {
		const buttons = [
			{ id: 'btn-state', view: 'State' },
			{ id: 'btn-central', view: 'Central' },
			{ id: 'btn-north', view: 'North' },
			{ id: 'btn-south', view: 'South' }
		];

		buttons.forEach(button => {
			const element = document.getElementById(button.id);
			if (element) {
				element.addEventListener('click', () => {
					this.vmi.enum('currentView').value = button.view;
					this.updateActiveButton(element);
				});
			}
		});
	}

	updateActiveButton(activeBtn) {
		// Remove active class from all buttons
		document.querySelectorAll('#btn-state, #btn-central, #btn-north, #btn-south').forEach(btn => {
			btn.classList.remove('text-warm-white', 'active');
		});
		// Add active class to clicked button
		activeBtn.classList.add('text-warm-white', 'active');
	}

	populateRegionData() {
		// Populate data for all regions
		Object.keys(this.regionsData).forEach(regionName => {
			const regionData = this.regionsData[regionName];

			try {
				// First check if the view model exists
				const viewModel = this.vmi.viewModel(regionName);
				if (!viewModel) {
					return;
				}

				// Set text data for each region
				try { viewModel.string('country').value = regionData.country; } catch (e) {}
				try { viewModel.string('distance').value = regionData.distance; } catch (e) {}
				try { viewModel.string('nearby').value = regionData.nearby; } catch (e) {}
				try { viewModel.string('famous').value = regionData.famous; } catch (e) {}

				// Set up click handler for "view region" button
				try {
					const urlTrigger = viewModel.trigger('openUrl');
					if (urlTrigger) {
						urlTrigger.on(() => {
							if (this.navigating) return;
							this.navigating = true;
							this.navigateToRegion(regionData.url, regionName);
						});
					}
				} catch (triggerError) {
					// Trigger doesn't exist
				}

			} catch (error) {
				// Region doesn't exist in Rive file
			}
		});
	}

	navigateToRegion(url, regionName) {
		if (url) {
			// Add small delay to keep UI feeling snappy but stable
			setTimeout(() => {
				window.location.href = url;
			}, 80);
		}
	}

	getWineRegionsData() {
		// Get data from CMS
		const regionsDataElement = document.getElementById('regions-data');

		if (regionsDataElement) {
			try {
				const cmsData = JSON.parse(regionsDataElement.textContent);
				return cmsData;
			} catch (error) {
				return {};
			}
		}

		return {};
	}
}

// Initialize the wine regions map when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
	new WineRegionsMap();
});

export default WineRegionsMap;
